/**
 * Previous-period comparison helpers (pure, no I/O).
 *
 * The comparison window is the immediately preceding window of equal length:
 *   - rolling presets (1d, 7d, 30d, 90d, 12m) and custom ranges: the same
 *     duration ending just before the current window starts
 *   - mtd: the same days of last month (Sep 1–28 → Aug 1–28; overflow clamps,
 *     so Mar 1–31 → Feb 1–28/29)
 *   - ytd: the same span of last year
 *
 * Used by `usePreviousPeriodMetrics`; exported for Pages / Sources tabs.
 */
import { subMonths, subYears } from 'date-fns'

export interface PeriodWindow {
  /** ISO instant, inclusive */
  start: string
  /** ISO instant, inclusive */
  end: string
}

/**
 * Deduped totals for one window, read from the aggregate traffic summary.
 * `visitors` is the endpoint's deduped period total, never a sum of rows.
 */
export interface PeriodTotals {
  /** Unique visitors (summary `total_sessions`, the number the Overview shows) */
  visitors: number
  /** Total views (pageviews) */
  views: number
  /** Bounce rate in percent (0–100), null when there were no visitors */
  bounceRate: number | null
  /** Average time on site in seconds, null when there were no visitors */
  avgTimeOnSiteSeconds: number | null
}

/**
 * Signed percent change from `previous` to `current`, rounded to an integer.
 * Returns null when either side is missing or non-finite, or when `previous`
 * is 0 or negative (a change from nothing has no meaningful percentage).
 */
export function computePeriodDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null {
  if (current == null || previous == null) return null
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null
  if (previous <= 0) return null
  return Math.round(((current - previous) / previous) * 100)
}

/**
 * Signed change in percentage points between two rates already expressed in
 * percent (e.g. bounce rate 42 → 45 = +3). Relative % change of a rate reads
 * wrong, so rates use points. Null when either side is missing.
 */
export function computePointDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null {
  if (current == null || previous == null) return null
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null
  return Math.round(current - previous)
}

function isMonthToDate(period: string): boolean {
  return period === 'mtd' || period === 'MONTH_TO_DATE'
}

function isYearToDate(period: string): boolean {
  return period === 'ytd' || period === 'YEAR_TO_DATE'
}

/**
 * The window to compare against, derived from the current window the page was
 * actually queried with. Returns null for an unparseable or empty window.
 */
export function getPreviousPeriodWindow(period: string, current: PeriodWindow): PeriodWindow | null {
  const start = new Date(current.start)
  const end = new Date(current.end)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  const span = end.getTime() - start.getTime()
  if (span <= 0) return null

  if (isMonthToDate(period)) {
    return { start: subMonths(start, 1).toISOString(), end: subMonths(end, 1).toISOString() }
  }
  if (isYearToDate(period)) {
    return { start: subYears(start, 1).toISOString(), end: subYears(end, 1).toISOString() }
  }

  // Equal-length window ending 1ms before the current one starts
  const prevEnd = start.getTime() - 1
  return {
    start: new Date(prevEnd - span).toISOString(),
    end: new Date(prevEnd).toISOString(),
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * How the comparison reads in a sentence, e.g. "the previous 7 days",
 * "the same days last month". Custom ranges derive the day count from the
 * current window.
 */
export function previousPeriodPhrase(period: string, current?: PeriodWindow | null): string {
  const map: Record<string, string> = {
    '1d': 'the previous 24 hours',
    PAST_24_H: 'the previous 24 hours',
    '7d': 'the previous 7 days',
    LAST_7_DAYS: 'the previous 7 days',
    '30d': 'the previous 30 days',
    LAST_30_DAYS: 'the previous 30 days',
    '90d': 'the previous 90 days',
    LAST_90_DAYS: 'the previous 90 days',
    '12m': 'the previous 12 months',
    LAST_12_MONTHS: 'the previous 12 months',
    mtd: 'the same days last month',
    MONTH_TO_DATE: 'the same days last month',
    ytd: 'the same period last year',
    YEAR_TO_DATE: 'the same period last year',
  }
  if (map[period]) return map[period]
  if (current) {
    const span = new Date(current.end).getTime() - new Date(current.start).getTime()
    if (Number.isFinite(span) && span > 0) {
      const days = Math.max(1, Math.round(span / DAY_MS))
      return days === 1 ? 'the previous day' : `the previous ${days} days`
    }
  }
  return 'the previous period'
}

/** Shape of the aggregate summary payload fields this module reads. */
export interface SummaryTotalsInput {
  total_sessions?: number | null
  page_views?: number | null
  bounce_count?: number | null
  total_session_time_ms?: number | null
}

/** Normalize an aggregate traffic summary into `PeriodTotals`. */
export function summaryToPeriodTotals(data: SummaryTotalsInput | null | undefined): PeriodTotals | null {
  if (!data) return null
  const visitors = Number(data.total_sessions ?? 0) || 0
  const views = Number(data.page_views ?? 0) || 0
  const bounces = Number(data.bounce_count ?? 0) || 0
  const timeMs = Number(data.total_session_time_ms ?? 0) || 0
  return {
    visitors,
    views,
    bounceRate: visitors > 0 ? (bounces / visitors) * 100 : null,
    avgTimeOnSiteSeconds: visitors > 0 ? timeMs / 1000 / visitors : null,
  }
}
